import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { ApiError } from "../../lib/api-client";
import { queryClient } from "../../lib/queryClient";
import { sessionExpired } from "../../lib/sessionExpired";
import { store } from "../../store";
import { allows } from "./logic";

// The forex backend's operator routes (handoffs 012–026). Unlike the rest of
// the forex pages these take the dashboard admin JWT as a Bearer token. The
// admin must hold a forex grant (403 otherwise), and since v1.10.0 each route
// also needs one permission from the operator's role (025).
const client = axios.create({
  baseURL: `${process.env.REACT_APP_FOREX_API_BASE_URL || "https://forex-py-stage.ginox.io"}/api/v1/forex`,
  timeout: 20000,
});

client.interceptors.request.use((config) => {
  const token = store.getState().auth.access_token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 429 answers { error } outside the envelope, so fall back to it.
client.interceptors.response.use(
  (r) => r,
  (error) => {
    const body = error.response?.data;
    // Nothing here is a login call, so every 401 is a dead admin token.
    if (error.response?.status === 401) sessionExpired();
    const message = body?.message || body?.error || error.message || "Request failed";
    // Any 403 mid session (012): ask /me again. A 403 there means the grant is
    // gone and the shell leaves forex; a 200 redraws the pages from what the role
    // holds now. Not for /me's own 403, which would only ask again forever.
    if (error.response?.status === 403 && !error.config?.url?.endsWith("/operators/admin/me"))
      queryClient.invalidateQueries({ queryKey: ["forex-admin", "me"] });
    return Promise.reject(new ApiError(error.response?.status ?? 0, message, body));
  },
);

// Admin queries are strict: a blank or unknown key is a 400, so blanks never go.
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params)
      .map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
      .filter(([, v]) => v !== "" && v != null),
  );

export const adminGet = (path, params) =>
  client.get(path, { params: clean(params) }).then((r) => r.data.data);

export const adminPost = (path, body) => client.post(path, body).then((r) => r.data.data);
export const adminPatch = (path, body) => client.patch(path, body).then((r) => r.data.data);
export const adminDelete = (path) => client.delete(path).then((r) => r.data.data);

// Retrying a 4xx only burns the 100/min operator budget; a 503 is worth one more go.
const retry = (count, err) => count < 1 && !(err?.status >= 400 && err?.status < 500);

// One hook for every admin read. No polling and no refetch on focus: the
// Refresh button is how an operator asks again. Pass path=null to hold a query.
export function useForexAdmin(path, params, opts = {}) {
  return useQuery({
    queryKey: ["forex-admin", path, params],
    queryFn: () => adminGet(path, params),
    enabled: !!path,
    retry,
    refetchOnWindowFocus: false,
    staleTime: 10_000,
    ...opts,
  });
}

// Who the signed-in admin is to forex. 403 = no grant, which is an answer, not
// a failure, so it is never retried.
export function useOperator() {
  const token = useSelector((s) => s.auth.access_token);
  return useQuery({
    queryKey: ["forex-admin", "me", token],
    queryFn: () => adminGet("/operators/admin/me"),
    enabled: !!token,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

// can("masters.hide"), or can([a, b]) for either. Reads /me's permissions
// (025), so nothing opens until /me has answered.
export function useCan() {
  const perms = useOperator().data?.permissions;
  return (need) => allows(perms, need);
}

const opts = (values, all) => [
  ...(all ? [{ value: "", label: all }] : []),
  ...values.map((v) => ({ value: v, label: v.replaceAll("_", " ") })),
];

export const KIND = opts(["REAL", "DEMO"], "All kinds");
export const TIER = opts(["STARTER", "ECN", "PRO"], "All tiers");
export const SCHEDULE = opts(["DAILY", "EVERY_2_DAYS", "EVERY_3_DAYS", "WEEKLY", "MONTHLY"]);
export const PURPOSE =opts(["REGULAR", "COPY_TRADING"], "All purposes");
// Lower case on the wire (v1.1.0); "SIGNAL" is a 400.
export const MASTER_TYPE = [
  { value: "", label: "All types" },
  { value: "customer", label: "Customer" },
  { value: "signal", label: "Signal" },
];
// v1.6.0 (024): exactly "true" / "false"; anything else is a 400, and "" is dropped.
export const MASTER_VISIBILITY = [
  { value: "", label: "Shown and hidden" },
  { value: "false", label: "Shown" },
  { value: "true", label: "Hidden" },
];
export const PAGE_SIZES = [20, 50, 100, 200].map((n) => ({ value: String(n), label: `${n} / page` }));
